import {
  useEffect, useState, useRef, useCallback
} from "react";
import type { Business, LensPlatformClient } from "lens-platform-sdk";
import { LensSDKException } from "lens-platform-sdk";
import { useLensPlatformClient } from "./useLensPlatformClient";

/**
 * Type for updating a business entity.
 */
export type NewBusiness = Parameters<LensPlatformClient["business"]["updateOne"]>[1];

/**
 * Hook for creating business entities (Lens Business IDs)
 */
export const useUpdateBusiness = () => {
  const lensPlatformClient = useLensPlatformClient();
  const [error, setError] = useState<LensSDKException | undefined>();
  const [updating, setUpdating] = useState(false);
  const [response, setResponse] = useState<Business | undefined>();

  // Keep up with mounted/unmounted state
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const updateBusiness = useCallback(async (id: string, business: NewBusiness) => {
    try {
      if (mounted.current) {
        setUpdating(true);
        setError(undefined);
      }
      const response = await lensPlatformClient.business.updateOne(id, business);

      if (mounted.current) {
        setResponse(response);
        setError(undefined);
      }

      return response;
    } catch (exception) {
      if (mounted.current && exception instanceof LensSDKException) {
        setError(exception);
      }

      return undefined;
    } finally {
      if (mounted.current) {
        setUpdating(false);
      }
    }
  }, [lensPlatformClient.business]);

  return {
    updateBusiness,
    response,
    updating,
    error
  };
};
